/**
 * Spec №15.3 — shared helpers for the Staff axis (the rail + the desk).
 *
 * Kept schema-free (X-4): the capability strip is derived at runtime from the
 * live 11.2 area/role data (`list_areas` + `list_roles` + `list_role_assignments`),
 * never from a hard-coded per-agent verb list. An agent is a machine member that
 * holds roles exactly like a human (11.2), so its "can do / cannot" is simply the
 * union of the areas its roles grant vs the money areas they do not — plus the one
 * universal machine-member wall: an autonomous actor can never self-approve a
 * ledger authorisation (I9 v2 `autonomous_cannot_approve`), so money it touches
 * always lands in the human loop.
 */
import type { AreaDecl, RoleRow, AssignmentRow } from "../../hooks/use-policy";

/** The roster avatar palette — the exact array the rail has always cycled by
 *  index, kept here as the ONE source so the desk avatar matches the rail row for
 *  the same agent. Order preserved (no snapshot churn). Real ramp tokens. */
export const AVATAR_COLORS = [
  "var(--c-teal-600)", "var(--c-purple-600)", "var(--c-blue-600)",
  "var(--c-coral-400)", "var(--c-amber-400)",
];
/** The desk resolves its avatar tint by the agent's position in the roster (the
 *  same index the rail uses), so rail row and desk header always agree. */
export function avatarColorFor(agentId: string, roster: { id: string }[]): string {
  const i = roster.findIndex((r) => r.id === agentId);
  const idx = i >= 0 ? i : 0;
  return AVATAR_COLORS[idx % AVATAR_COLORS.length]!;
}

/** Presence dot mapping (D-ST5 · N2) — passive, derived from Activity recency by
 *  the server (`get_workforce_status`). No liveness is simulated. */
export function presenceDotClass(status: string | undefined): string {
  if (status === "working") return "is-working";
  if (status === "awaiting" || status === "scheduled") return "is-scheduled";
  return "is-idle";
}
export function presenceLabel(status: string | undefined): string {
  if (status === "working") return "Working";
  if (status === "awaiting" || status === "scheduled") return "Reviewing";
  return "Available";
}

export interface Capabilities {
  /** role names this agent holds (11.2, same as a human). */
  roles: string[];
  /** areas the agent can act in (granted by its roles ∪ default_member). */
  can: AreaDecl[];
  /** money areas it does NOT hold — the concrete wall. */
  wall: AreaDecl[];
  /** money areas it CAN operate in (so the I9 authorisation wall applies). */
  moneyGranted: AreaDecl[];
  /** the headline "cannot" + why, derived from the granted/withheld area keys. */
  wallHead: string;
  wallReason: string;
}

/**
 * Derive an agent's capabilities purely from live policy data. `assignments`,
 * `roles` and `areasData` come straight off the workspace verbs — nothing here
 * knows a specific agent, verb or role by name.
 */
export function deriveCapabilities(
  agentId: string,
  assignments: AssignmentRow[],
  roles: RoleRow[],
  areasData: { areas: AreaDecl[]; default_member: string[] } | undefined,
): Capabilities {
  const areas = areasData?.areas ?? [];
  const defaultMember = areasData?.default_member ?? [];
  const areaByKey = new Map(areas.map((a) => [a.key, a]));
  const roleByName = new Map(roles.map((r) => [r.name, r]));

  const held = assignments
    .filter((a) => a.subject_kind === "agent" && a.subject_id === agentId)
    .map((a) => a.role_name);

  const grantedKeys = new Set<string>(defaultMember);
  for (const name of held) for (const k of roleByName.get(name)?.areas ?? []) grantedKeys.add(k);

  const can = [...grantedKeys].map((k) => areaByKey.get(k)).filter((a): a is AreaDecl => !!a);
  const moneyGranted = can.filter((a) => a.money);
  const wall = areas.filter((a) => a.money && !grantedKeys.has(a.key));

  // The headline wall, read off the area keys the agent holds / lacks.
  let wallHead: string;
  let wallReason: string;
  if (grantedKeys.has("trust.operate")) {
    // Holds trust operate → can DRAFT, but as a machine member is never a
    // signatory and can never approve (I9 v2). This is the statutory wall.
    wallHead = "approve · post";
    wallReason = "never a signatory · autonomous_cannot_approve — every trust movement lands in the human I9 loop";
  } else if (grantedKeys.has("billing.operate") && !grantedKeys.has("billing.post")) {
    wallHead = "post invoices";
    wallReason = "billing.post not granted — a post attempt is refused server-side";
  } else if (moneyGranted.length === 0) {
    wallHead = "move money";
    wallReason = "holds no money area — cannot post or approve any ledger or billing action";
  } else {
    wallHead = "approve · post";
    wallReason = "autonomous_cannot_approve — prepared work lands in the human authorisation loop";
  }

  return { roles: held, can, wall, moneyGranted, wallHead, wallReason };
}
